import * as yup from 'yup'

import { Box, ListItemText, Switch } from '@mui/material'
import InputModal, { InputModalGrid } from 'components/common/InputModal'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import {
  currentWellAtom,
  currentWellDetailsSelector,
  currentWellSelector,
  userPrefsAtom,
  userUserRoleAtom,
} from 'atoms'
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil'

import AlertsModal from 'components/Alerts/AlertsModal'
import ChatWindow from 'components/ChatWindow/ChatWindow'
import NavBarIcon from './NavBarIcon'
import StatusSnackbar from 'components/common/StatusSnackbar'
import UserProfileCard from './UserProfileCard'
import UserProfileModal from 'components/UserProfileModal/UserProfileModal'
import { addItemToLS } from 'utils/localStorage'
import { alpha } from '@mui/material/styles'
import { appColors } from 'utils'
import { checkFeature } from 'components/userPermissions'
import { useAuth0 } from '@auth0/auth0-react'
import { useChatData } from 'components/contexts/ChatContext'
import useInnovaAuth from 'components/common/hooks/useInnovaAuth'
import useInnovaAxios from 'components/common/hooks/useInnovaAxios'
import useInnovaTheme from 'components/common/hooks/useInnovaTheme'
import { useNavigate } from 'react-router-dom'
import useObjectsOfInterest from 'components/common/hooks/useObjectsOfInterest'
import useUnits from 'components/common/hooks/useUnits'
import useUserDatabaseAccess from 'components/common/hooks/useUserDatabaseAccess'

const getInitials = (name = '') => {
  const parts = String(name).trim().split(/\s+/).filter(Boolean)

  if (parts.length === 0) return '?'
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()

  const first = parts[0][0] || ''
  const last = parts[parts.length - 1][0] || ''
  return (first + last).toUpperCase()
}

export const InitialsAvatarIcon = ({ name, size = 30, fontSize = 16, title }) => {
  const initials = useMemo(() => getInitials(name), [name])

  return (
    <Box
      title={title}
      sx={{
        width: size,
        height: size,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',

        color: '#000',
        fontSize,
        fontWeight: 700,
        letterSpacing: '0.5px',
        lineHeight: 1,
      }}>
      {initials}
    </Box>
  )
}

const NavBarUserProfile = ({ isAuthenticated, logout }) => {
  const _isMounted = useRef(false)
  const [showObjectsOfInterestModal, setShowObjectsOfInterestModal] = useState(false)
  const [showUnitsModal, setUnitsModal] = useState(false)
  const [showPasswordModal, setPasswordModal] = useState(false)
  const [userPrefs, setUserPrefs] = useRecoilState(userPrefsAtom)
  const [status, setStatus] = useState({ show: false, severity: 'info', message: '' })
  const [showUserProfile, setShowUserProfile] = useState(false)
  const { authType, saveDatabaseOrg } = useInnovaAuth()
  const { user } = useAuth0()
  const { getLogoColor, toggleTheme, getTextColor, theme } = useInnovaTheme()
  const { chat } = useChatData()
  const [chatToggle, setChatToggle] = useState(false)
  const { unitListOrg } = useUnits() // eslint-disable-line no-unused-vars
  const navigate = useNavigate()
  const { getUserDatabaseAccessList, hasMultiDbAccess, getCurrentDatabaseOrg, setAccessDatabase } =
    useUserDatabaseAccess(true) // since RecoilData is gone, init fetch here.

  const setCurrentWell = useSetRecoilState(currentWellSelector)
  const { dropDowns, objectsOfInterest, updateUserObjectsOfInterest } = useObjectsOfInterest()
  const currentWell = useRecoilValue(currentWellAtom)
  const setCurrentWellDetails = useSetRecoilState(currentWellDetailsSelector)

  const [alertsModal, setAlertsModal] = useState(false)
  const userRole = useRecoilValue(userUserRoleAtom)

  const updateUnits = useInnovaAxios({
    url: '/user/storePrefs',
  })

  const updatePwd = useInnovaAxios({
    url: '/admin/resetUserPwd',
  })

  const fetchUserPrefs = useInnovaAxios({
    url: '/user/getPrefs',
  })

  useEffect(() => {
    _isMounted.current = true

    if (hasMultiDbAccess()) {
      const dbList = getUserDatabaseAccessList()
      if (dbList.includes(currentWell?.organization)) {
        saveDatabaseOrg(currentWell?.organization ? currentWell.organization : '')
      }
    }
    return () => {
      _isMounted.current = false
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const openChat = (event) => {
    event.stopPropagation()
    setChatToggle(true)
  }

  const onClickLogout = () => {
    logout()
  }

  const onClickUserProfile = () => {
    setShowUserProfile(true)
  }

  const hasAlertsFeature = checkFeature(51, userRole?.roleAttributes?.featureId)

  const getPickListObjArray = (objArray) => {
    if (!Array.isArray(objArray)) return []

    let newObjArray = []
    objArray.forEach((item) => {
      newObjArray.push({
        id: item.value,
        desc: item.label,
      })
    })
    return newObjArray.sort((a, b) => a.desc.localeCompare(b.desc))
  }

  const getPickListValArray = (objArray) => {
    if (!Array.isArray(objArray)) return []

    let newObjArray = []
    objArray.forEach((item) => {
      newObjArray.push({
        label: item,
        desc: item,
      })
    })
    return newObjArray.sort((a, b) => a.desc.localeCompare(b.desc))
  }

  const ThemeButton = ({ color }) => {
    return (
      <Box sx={{ paddingLeft: '3px', display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 1 }}>
        <Switch checked={theme === 'dark'} onChange={() => toggleTheme()} />
        <ListItemText
          sx={{
            '& .MuiListItemText-primary': {
              fontSize: '16px',
              fontFamily: 'Roboto',
            },
            color,
          }}>
          {'Dark Mode'}
        </ListItemText>
      </Box>
    )
  }

  const ObjectsOfInterestModal = () => {
    let useInputData = false
    if (
      objectsOfInterest &&
      objectsOfInterest.hasOwnProperty('rigs') &&
      objectsOfInterest.hasOwnProperty('operators') &&
      objectsOfInterest.hasOwnProperty('districts')
    ) {
      useInputData = true
    }

    let formData = [
      {
        tag: 'rigs',
        value: useInputData ? getPickListValArray(objectsOfInterest?.rigs) : '',
        inputType: 'picklist',
        pickListValues: getPickListObjArray(dropDowns?.rigs),
        text: 'Rigs',
      },
      {
        tag: 'operators',
        value: useInputData ? getPickListValArray(objectsOfInterest?.operators) : [],
        inputType: 'picklist',
        pickListValues: getPickListObjArray(dropDowns?.operators),
        text: 'Operators',
      },
      {
        tag: 'districts',
        value: useInputData ? getPickListValArray(objectsOfInterest?.districts) : [],
        inputType: Array.isArray(dropDowns?.districts) && dropDowns?.districts.length > 0 ? 'picklist' : '-',
        pickListValues: getPickListObjArray(dropDowns?.districts),
        text: 'Districts',
      },
    ]

    return (
      <InputModalGrid
        open={showObjectsOfInterestModal}
        onClose={() => setShowObjectsOfInterestModal(false)}
        title='Favorite'
        formData={formData}
        submitForm={handleChangeObjectsOfInterest}
        cancelFunction={() => setShowObjectsOfInterestModal(false)}
      />
    )
  }

  const ChangeUnitsModal = () => {
    const formValidationSchema = yup.object({
      unitPrefs: yup.string().required('units required'),
    })

    let unitPrefValue = 0
    for (let i = 0; i < unitListOrg.length; i++) {
      if (unitListOrg[i].label === userPrefs?.unitPrefs) {
        unitPrefValue = unitListOrg[i].value
        break
      }
    }

    let formData = [
      {
        tag: 'unitPrefs',
        value: userPrefs ? unitPrefValue : '',
        text: 'Units',
        inputType: 'dropdown',
        dropDownValues: unitListOrg,
        useLabel: true,
      },
    ]

    return (
      <InputModal
        open={showUnitsModal}
        onClose={() => setUnitsModal(false)}
        title={'Change units'}
        formData={formData}
        submitForm={handleChangeUnits}
        cancelFunction={() => setUnitsModal(false)}
        validationSchema={formValidationSchema}
      />
    )
  }

  const ChangePasswordModal = () => {
    const formValidationSchema = yup.object({
      password: yup
        .string()
        .required('Password is required')
        .matches(
          /^(?=.*[A-Za-z])(?=.*\d)(?=.*[@$!%*#?&-])[A-Za-z\d@$!%*#?&-]{8,}$/,
          'Must Contain 8 Characters, One Uppercase, One Lowercase, One Number and one special case Character',
        ),
      passwordConfirmation: yup.string().oneOf([yup.ref('password'), null], 'Passwords must match'),
    })

    let formData = [
      {
        tag: 'authUserName',
        value: user?.name,
        inputType: '-',
      },
      {
        tag: 'password',
        value: '',
        text: 'New Password',
        inputType: 'text',
      },
      {
        tag: 'passwordConfirmation',
        value: '',
        text: 'Confirm Password',
        inputType: 'text',
      },
    ]

    return (
      <InputModal
        open={showPasswordModal}
        onClose={() => {
          setPasswordModal(false)
        }}
        title={'Change Password'}
        formData={formData}
        submitForm={handleChangePassword}
        cancelFunction={() => {
          setPasswordModal(false)
        }}
        validationSchema={formValidationSchema}
      />
    )
  }

  const handleChangeObjectsOfInterest = async (data) => {
    setShowObjectsOfInterestModal(false)
    if (!data) return false

    let res = await updateUserObjectsOfInterest({ ...data })
    if (res?.error) {
      setStatus({
        show: true,
        severity: 'error',
        message: 'Failed to update objects of interest',
      })

      return
    }

    setStatus({ show: true, severity: 'success', message: 'Objects of interest updated' })
  }

  const handleChangeUnits = async (data) => {
    setUnitsModal(false)
    if (!data) return

    for (let i = 0; i < unitListOrg.length; i++) {
      if (unitListOrg[i].value === data.unitPrefs) {
        data.unitPrefs = unitListOrg[i].label
        break
      }
    }

    let res = await updateUnits({ ...data })
    if (!_isMounted.current) return

    if (res?.error) {
      setStatus({
        show: true,
        severity: 'error',
        message: 'Failed to update units',
      })

      return
    }

    setStatus({ show: true, severity: 'success', message: 'Units updated' })

    res = await fetchUserPrefs()
    if (!_isMounted.current) return
    if (!res?.data) return

    setUserPrefs(res.data)
    if (res.data?.unitPrefs != null) {
      addItemToLS('units', 'selectedUnits', res.data?.unitPrefs)
    }

    window.location.reload() //refresh the entire page to response to updated units across global data
  }

  const handleChangePassword = async (data) => {
    setPasswordModal(false)
    if (!data) return

    let res = await updatePwd({
      ...data,
      sendEmail: true,
      incPwdInEmail: false,
      resetRequiredOnNextLogin: false,
    })

    if (!_isMounted.current) return
    if (res?.error) {
      setStatus({
        show: true,
        severity: 'error',
        message: 'Failed to update password',
      })
      return
    }

    setStatus({ show: true, severity: 'success', message: 'Password updated' })
  }

  const dbAccessList = useMemo(() => {
    let databases = getUserDatabaseAccessList()
    if (!Array.isArray(databases)) return []
    let databaseOrg = getCurrentDatabaseOrg()
    return databases.map((item) => {
      return {
        onClick: () => {
          if (item === databaseOrg) return
          setAccessDatabase(item)
          setCurrentWell({
            wellName: '',
            organization: item,
          })
          setCurrentWellDetails({
            actualWell: '',
            wellStatus: '',
            operator: '',
            rig: '',
            jobNum: '',
            lat: '',
            lng: '',
          })
          navigate('/well-list')
          window.location.reload()
        },
        label: item,
        icon: 'material-symbols-light:database',
        color: item === databaseOrg ? appColors.itemTextColor : getTextColor(),
        selected: item === databaseOrg,
      }
    })
  }, [
    navigate,
    getUserDatabaseAccessList,
    getCurrentDatabaseOrg,
    setAccessDatabase,
    setCurrentWell,
    setCurrentWellDetails,
    getTextColor,
  ])

  const getNameFromEmail = (email) => {
    if (typeof email !== 'string' || email.length <= 0) return ''
    const parts = email.split('@')
    if (parts.length <= 0) return ''
    return parts[0].replace(/\./g, ' ') || ''
  }

  return isAuthenticated ? (
    <React.Fragment>
      {alertsModal ? (
        <AlertsModal
          onCancel={() => setAlertsModal(false)}
          byUser={true}
          byRole={true}
          roleId={userRole.id ? userRole.id.toString() : ''}
          userName={userRole?.userPrefs.userName ? userRole.userPrefs.userName : ''}
        />
      ) : null}
      {showUnitsModal ? <ChangeUnitsModal /> : null}
      {showObjectsOfInterestModal ? <ObjectsOfInterestModal /> : null}
      {showPasswordModal ? <ChangePasswordModal /> : null}
      {showUserProfile ? <UserProfileModal open={showUserProfile} onClose={() => setShowUserProfile(false)} /> : null}
      <Box sx={{ display: 'flex', flexDirection: 'row', paddingRight: 1, gap: 1 }}>
        <NavBarIcon
          title={'Help'}
          icon={'material-symbols:help'}
          onClick={() =>
            window.open(
              'https://docs.innova-drilling.com/introduction/video-guides/how-to-videos/web-portal',
              '_blank',
              'noreferrer',
            )
          }
        />
        <NavBarIcon
          title={'Chat'}
          icon={'material-symbols:chat'}
          onClick={(event) => openChat(event)}
          badgeContent={chat.unreadMessages ? chat.unreadMessages.toString() : null}
        />
        <NavBarIcon
          title={'Organization'}
          icon={'material-symbols-light:database'}
          menuItems={dbAccessList}
          visible={hasMultiDbAccess()}
        />
        <NavBarIcon
          title={
            <Box sx={{ display: 'flex', flexDirection: 'column', lineHeight: 1.2 }}>
              <Box sx={{ fontWeight: 600, fontSize: '12px' }}>{user?.preferred_username}</Box>
              <Box sx={{ fontSize: '12px', opacity: 0.8 }}>{userRole.organization}</Box>
              <Box sx={{ fontSize: '12px', opacity: 0.8 }}>{userRole?.userPrefs?.unitPrefs}</Box>
            </Box>
          }
          backgroundColor={alpha(getLogoColor(), 0.7)}
          hoverBackgroundColor={alpha(getLogoColor(), 0.9)}
          icon={<InitialsAvatarIcon name={user?.name || getNameFromEmail(user?.email)} />}
          menuItems={(() => {
            const items = [
              { component: <UserProfileCard /> },
              { component: <ThemeButton color={theme === 'dark' ? appColors.headerTextColor : '#151515'} /> },
              { onClick: () => onClickUserProfile(), label: 'Profile', icon: 'healthicons:ui-user-profile' },
              {
                onClick: () => setShowObjectsOfInterestModal(true),
                label: 'Favorites',
                icon: 'material-symbols:star',
                color: 'gold',
              },
              {
                onClick: () => setAlertsModal(true),
                hide: !hasAlertsFeature,
                label: 'Alerts',
                icon: 'vaadin:bell',
              },
              {
                onClick: () => setUnitsModal(true),
                label: 'Change Units',
                icon: unitListOrg?.length <= 0 ? 'svg-spinners:ring-resize' : 'fa:balance-scale',
                disabled: unitListOrg?.length <= 0,
              },
            ]
            if (authType === 'Innova') {
              items.push({
                onClick: () => setPasswordModal(!showPasswordModal),
                label: 'Change Password',
                icon: 'material-symbols:password',
              })
            }
            items.push({ onClick: () => onClickLogout(), label: 'Logout', icon: 'material-symbols:logout' })
            return items
          })()}
        />
      </Box>
      <ChatWindow open={chatToggle} onClose={() => setChatToggle(false)} />
      <StatusSnackbar status={status} setStatus={setStatus} />
    </React.Fragment>
  ) : null
}

export default NavBarUserProfile
